import { Handle, Position } from '@xyflow/react'
import { isFired } from './useFiredFlash'
import { VectorField } from './WayfindingNodes'

/**
 * Character → Transform nodes (Unreal-style). "The character" is the avatar's
 * root group (movementComponent.getGroup()). Getters are pure data-output nodes
 * (no exec) that emit a [x,y,z] vector / rotator / transform; setters are exec
 * actions that read an input vector and write the group. Runtime lives in
 * EventRuntime.js (getters in _resolveDataPin, setters in the exec handler).
 *
 * Convention (see memory feedback-node-unreal-convention): every typed input is
 * a pin OR an inline manual value (VectorField collapses when wired); vectors
 * lay out horizontally; descriptions are hover tooltips, not inline text.
 */

const GET_ACCENT = '#7ad29a'   // green — pure getters
const SET_ACCENT = '#e0a060'   // amber — mutating setters
const vecPin = { background: '#60a0d0', width: 10, height: 10, borderRadius: '50%', border: '2px solid #60a0d044' }
const execStyle = { background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }

function Shell({ selected, fired, accent, icon, label, tip, exec, minWidth = 200, children }) {
  return (
    <div title={tip} style={{
      background: selected ? '#0f1a12' : '#0a120c',
      border: `2px solid ${fired ? '#ffcc44' : selected ? accent : '#1d3326'}`,
      borderRadius: 6, minWidth, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      <div style={{
        background: selected ? '#16271b' : '#102015', padding: '6px 12px',
        display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
      }}>
        {exec && <Handle type="target" position={Position.Left} id="execIn" style={execStyle} />}
        <span style={{ fontSize: 13 }}>{icon}</span>
        <span style={{ color: accent, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>{label}</span>
        {exec && <Handle type="source" position={Position.Right} id="execOut" style={execStyle} />}
      </div>
      {children}
    </div>
  )
}

// A getter: header + a single right-side data output pin labelled `outLabel`.
function Getter({ icon, label, outLabel, tip, selected, fired }) {
  return (
    <Shell selected={selected} fired={fired} accent={GET_ACCENT} icon={icon} label={label} tip={tip}>
      <div style={{ padding: '6px 12px 8px', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
        <span style={{ color: '#4a7a5c', fontSize: 10 }}>{outLabel}</span>
        <Handle type="source" position={Position.Right} id="out-value" style={vecPin} />
      </div>
    </Shell>
  )
}

export function GetCharacterLocationNode({ data, selected }) {
  return <Getter selected={selected} fired={isFired(data)} icon="📍" label="Get Character Location"
    outLabel="Location [x,y,z]" tip="Avatar world position as a vector [x,y,z] (UE GetActorLocation)." />
}
export function GetCharacterRotationNode({ data, selected }) {
  return <Getter selected={selected} fired={isFired(data)} icon="🧭" label="Get Character Rotation"
    outLabel="Rotation [x,y,z]°" tip="Avatar rotation as Euler degrees [x,y,z] (UE GetActorRotation)." />
}
export function GetCharacterTransformNode({ data, selected }) {
  return <Getter selected={selected} fired={isFired(data)} icon="🧩" label="Get Character Transform"
    outLabel="Transform" tip="Avatar transform { location, rotation, scale } (UE GetActorTransform). Wire into Set Character Transform." />
}
export function GetCharacterForwardNode({ data, selected }) {
  return <Getter selected={selected} fired={isFired(data)} icon="➡️" label="Get Character Forward"
    outLabel="Forward [x,y,z]" tip="Avatar forward unit vector (UE GetActorForwardVector)." />
}
export function GetCharacterRightNode({ data, selected }) {
  return <Getter selected={selected} fired={isFired(data)} icon="↪️" label="Get Character Right"
    outLabel="Right [x,y,z]" tip="Avatar right unit vector (UE GetActorRightVector)." />
}
export function GetCharacterUpNode({ data, selected }) {
  return <Getter selected={selected} fired={isFired(data)} icon="⬆️" label="Get Character Up"
    outLabel="Up [x,y,z]" tip="Avatar up unit vector (UE GetActorUpVector)." />
}

export function SetCharacterLocationNode({ id, data, selected }) {
  return (
    <Shell selected={selected} fired={isFired(data)} accent={SET_ACCENT} exec minWidth={236} icon="📌" label="Set Character Location"
      tip="Teleport the avatar to a world position (UE SetActorLocation). Wire in-location, or type X/Y/Z.">
      <VectorField nodeId={id} label="Location" pinId="in-location"
        x={data.x} y={data.y} z={data.z} fields={['x', 'y', 'z']}
        onChange={(field, v) => data.onChange?.(field, v)} />
    </Shell>
  )
}
export function SetCharacterRotationNode({ id, data, selected }) {
  return (
    <Shell selected={selected} fired={isFired(data)} accent={SET_ACCENT} exec minWidth={236} icon="🔄" label="Set Character Rotation"
      tip="Set the avatar rotation in Euler degrees (UE SetActorRotation). Wire in-rotation, or type X/Y/Z.">
      <VectorField nodeId={id} label="Rotation°" pinId="in-rotation"
        x={data.rx} y={data.ry} z={data.rz} fields={['rx', 'ry', 'rz']}
        onChange={(field, v) => data.onChange?.(field, v)} />
    </Shell>
  )
}
export function SetCharacterTransformNode({ data, selected }) {
  return (
    <Shell selected={selected} fired={isFired(data)} accent={SET_ACCENT} exec minWidth={210} icon="🧱" label="Set Character Transform"
      tip="Apply a full transform { location, rotation, scale } to the avatar (UE SetActorTransform). Wire in-transform from Get Character Transform.">
      <div style={{ padding: '6px 12px 8px', position: 'relative', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Handle type="target" position={Position.Left} id="in-transform" style={vecPin} />
        <span style={{ color: '#7a5a30', fontSize: 10 }}>Transform (wire)</span>
      </div>
    </Shell>
  )
}
